/**
 * EnergyMultiSiteView — Vue société consolidée
 *
 * S'affiche automatiquement quand le prospect possède >= 2 sites énergétiques.
 * Additionne les surfaces, somme les consommations par année, calcule un
 * statut agrégé (le pire l'emporte), un score moyen pondéré par surface,
 * et affiche un mini-tableau par site avec statut individuel.
 *
 * Toggle repliable (fermé par défaut).
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, ChevronDown, ChevronUp, MapPin, TrendingUp, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_STYLE = {
    maitrisee:         'bg-emerald-100 text-emerald-800 border-emerald-200',
    surveillance:      'bg-blue-100 text-blue-800 border-blue-200',
    action_recommandee:'bg-amber-100 text-amber-800 border-amber-300',
    prioritaire:       'bg-orange-100 text-orange-800 border-orange-300',
    regularisation:    'bg-rose-100 text-rose-800 border-rose-300',
    insuffisant:       'bg-slate-100 text-slate-700 border-slate-300',
};

const STATUS_LABEL = {
    maitrisee: 'Maîtrisée', surveillance: 'Surveillance',
    action_recommandee: 'Action recommandée', prioritaire: 'Prioritaire',
    regularisation: 'Régularisation', insuffisant: 'Insuffisant',
};

function fmt(n, unit = '') {
    if (n == null) return '—';
    const rounded = Math.round(n * 10) / 10;
    return `${rounded.toLocaleString('fr-FR')} ${unit}`.trim();
}

export default function EnergyMultiSiteView({ prospectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/prospects/${prospectId}/energy-cards/consolidated`);
                if (!cancelled) setData(res.data);
            } catch { if (!cancelled) setData(null); }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [prospectId]);

    if (loading || !data) return null;
    // Ne s'affiche PAS si moins de 2 sites
    if ((data.sites_count || 0) < 2) return null;

    const c = data.consolidated || {};
    const worstStyle = STATUS_STYLE[c.worst_status] || STATUS_STYLE.insuffisant;
    const worstLabel = STATUS_LABEL[c.worst_status] || '—';

    // Graph : conso totale par année
    const chartData = Object.entries(c.conso_by_year || {})
        .map(([year, kwh]) => ({ year: Number(year), kwh }))
        .sort((a, b) => a.year - b.year);

    return (
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/40" data-testid="energy-multi-site-view">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                            <Building2 className="w-4 h-4 text-white" />
                        </div>
                        Vue société consolidée
                        <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 ml-1">
                            {data.sites_count} sites
                        </Badge>
                    </CardTitle>
                    <Button
                        size="sm" variant="ghost"
                        onClick={() => setExpanded((v) => !v)}
                        data-testid="multi-site-toggle"
                    >
                        {expanded ? <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Réduire</> : <><ChevronDown className="w-3.5 h-3.5 mr-1" /> Voir le détail</>}
                    </Button>
                </div>
            </CardHeader>

            <CardContent className="space-y-3">
                {/* Chiffres clés */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 rounded-lg bg-white border border-indigo-100">
                        <div className="text-slate-400 uppercase tracking-wide font-semibold text-[10px]">Surface tertiaire</div>
                        <div className="text-lg font-black text-indigo-700 mt-0.5">{fmt(c.surface_tertiaire_total, 'm²')}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white border border-indigo-100">
                        <div className="text-slate-400 uppercase tracking-wide font-semibold text-[10px]">Surface totale</div>
                        <div className="text-lg font-black text-slate-800 mt-0.5">{fmt(c.surface_totale, 'm²')}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white border border-indigo-100">
                        <div className="text-slate-400 uppercase tracking-wide font-semibold text-[10px]">Puissance CVC</div>
                        <div className="text-lg font-black text-amber-700 mt-0.5">{fmt(c.puissance_cvc_total_kw, 'kW')}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white border border-indigo-100">
                        <div className="text-slate-400 uppercase tracking-wide font-semibold text-[10px]">Score moyen</div>
                        <div className="text-lg font-black text-purple-700 mt-0.5">
                            {c.avg_opportunity_score != null ? `${c.avg_opportunity_score}/100` : '—'}
                        </div>
                    </div>
                </div>

                {/* Statuts agrégés */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-slate-500 font-semibold uppercase tracking-wide">Statut consolidé :</span>
                    <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold border', worstStyle)} data-testid="multi-worst-status">
                        {worstLabel} (site le plus critique)
                    </span>
                    {c.assujetti_count > 0 && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                            {c.assujetti_count}/{data.sites_count} assujetti(s) EET
                        </Badge>
                    )}
                    {c.bacs_non_conforme_count > 0 && (
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                            <AlertTriangle className="w-3 h-3 mr-1" /> {c.bacs_non_conforme_count} BACS non conforme(s)
                        </Badge>
                    )}
                </div>

                {/* Graph conso consolidée */}
                {chartData.length > 0 && (
                    <div className="rounded-lg bg-white border border-indigo-100 p-2">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1 px-1">
                            <TrendingUp className="w-3 h-3" /> Consommation cumulée tous sites (kWh/an)
                        </div>
                        <div className="h-32">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} width={50} />
                                    <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} kWh`} />
                                    <Line type="monotone" dataKey="kwh" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Détail par site (repliable) */}
                {expanded && c.per_site?.length > 0 && (
                    <div className="rounded-lg bg-white border border-indigo-100 overflow-hidden" data-testid="multi-site-per-site-table">
                        <div className="text-xs px-2 py-1.5 border-b bg-slate-50 font-semibold uppercase tracking-wide text-slate-500">
                            Détail par site ({c.per_site.length})
                        </div>
                        <div className="divide-y divide-slate-100">
                            {c.per_site.map((s) => {
                                const st = STATUS_STYLE[s.overall_status] || STATUS_STYLE.insuffisant;
                                const stLabel = STATUS_LABEL[s.overall_status] || '—';
                                return (
                                    <div key={s.site_id} className="flex items-center justify-between px-2 py-1.5 gap-2 text-xs hover:bg-slate-50">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <MapPin className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold text-slate-800 truncate">{s.label}</div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {s.city && `${s.city} · `}
                                                    {s.surface_tertiaire_m2 ? `${s.surface_tertiaire_m2} m²` : '—'}
                                                    {s.activity_dominant && ` · ${s.activity_dominant}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {s.reduction_actuelle_pct != null && (
                                                <span className="text-[10px] text-slate-500">-{s.reduction_actuelle_pct.toFixed?.(1) ?? s.reduction_actuelle_pct}%</span>
                                            )}
                                            {s.opportunity_score != null && (
                                                <span className="text-[10px] font-bold text-purple-700">{s.opportunity_score}/100</span>
                                            )}
                                            <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold border', st)}>
                                                {stLabel}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <p className="text-[10px] text-slate-400 italic">
                    Additions et statuts consolidés. Chaque site conserve sa Carte d&apos;identité individuelle recalculée en continu.
                </p>
            </CardContent>
        </Card>
    );
}
